// THE BRIDGE — rule-based founder coach.
//
// Deterministic. It reads only recorded state and says what that state implies.
// It is not motivational, it does not congratulate activity, and it will not
// invent external facts. Where it cannot know something, it says so.

import { weightOf, num, fmtMoney, fmtMonths } from './derive';
import { businessDaysBetween, addDays, withinRange } from './dates';
import { candidateScore } from './funnel';
import { orgCoachHints, orgPrimaryDealEnabled, orgReviewConcentrate } from './orgPlan';

const candidateTotal = (opp) => candidateScore(opp).total;

const CHALLENGE = 'challenge';
const OBSERVATION = 'observation';
const DIRECTION = 'direction';

export function coachMessages(state, ctx) {
  const { sprint, runway, evidence, d001, saudi, infra, time, top3 } = ctx;
  const out = [];
  const say = (id, tone, text, priority = 5) => out.push({ id, tone, text, priority });

  const cur = state.settings?.currency || '';

  // --- Conversion vs volume -------------------------------------------------
  const weekE1 = evidence.thisWeek.filter((e) => e.level === 'E1').length;
  const weekE3plus = evidence.thisWeek.filter((e) => weightOf(e.level) >= 3).length;
  const weekActivity = evidence.thisWeek.filter((e) => e.level === 'ACT').length;

  if (weekE1 >= 3 && weekE3plus === 0) {
    say(
      'conversion',
      CHALLENGE,
      `You recorded ${weekE1} engagements this week and none reached E3. The problem appears to be conversion, not top-of-funnel volume. Adding more meetings will not fix this.`,
      1
    );
  }
  if (weekActivity >= 5 && evidence.weekProof.length === 0) {
    say(
      'motion',
      CHALLENGE,
      `${weekActivity} activities logged this week and zero evidence. That is motion. The week has produced nothing the market has confirmed.`,
      1
    );
  }

  // --- Founder time ---------------------------------------------------------
  if (time.totalHours > 0) {
    const dubai = time.rows.find((r) => r.key === 'dubai');
    const other = time.rows.find((r) => r.key === 'other');
    const internalHours = time.totalHours - time.external;
    if (internalHours > time.external) {
      say(
        'time-internal',
        CHALLENGE,
        `You spent ${round(internalHours)} founder hours on internal work and ${round(time.external)} hours with external counterparties. That contradicts the sprint strategy.`,
        2
      );
    }
    if (dubai && dubai.actual < 40) {
      say(
        'time-dubai',
        CHALLENGE,
        `Dubai received ${dubai.actual}% of founder time against a 55% target. Dubai is the only market expected to produce a transaction in this sprint.`,
        2
      );
    }
    if (other && other.actual > 10) {
      say(
        'time-other',
        OBSERVATION,
        `${other.actual}% of founder time went to parked markets and administration against a 5% ceiling. Founder time is drifting away from external proof creation.`,
        3
      );
    }
  } else if (sprint.day > 3) {
    say('time-missing', OBSERVATION, 'No founder hours logged this week. Without it there is no way to test whether attention matches strategy.', 6);
  }

  // --- Saudi discipline -----------------------------------------------------
  if (saudi.count < 2 && (state.saudi?.opportunities || []).length > (infra.live.length || 0)) {
    say(
      'saudi-drift',
      CHALLENGE,
      `Saudi has ${saudi.count} of 4 signals but is absorbing more pipeline attention than Infrastructure. Saudi has not earned that yet.`,
      2
    );
  }
  if (saudi.unsupported.length) {
    say(
      'saudi-unsupported',
      CHALLENGE,
      `${saudi.unsupported.length} Saudi signal(s) are set above "No evidence" with nothing recorded to support them. A signal without a named counterparty is an opinion.`,
      3
    );
  }
  const openException = (state.exceptions || []).find((e) => !e.decision || e.decision === 'Pending');
  if (openException) {
    say(
      'exception-open',
      DIRECTION,
      `A Strategy Exception is open (${openException.trigger}). Until it is decided, the locked allocation stands. Do not begin the work in anticipation of approval.`,
      2
    );
  } else if (saudi.count >= 3 && sprint.day < 30) {
    say(
      'saudi-strong',
      OBSERVATION,
      `Saudi is carrying ${saudi.count} strong signals before Day 30. That is the one pattern that would justify a Strategy Exception — but it needs the form, not enthusiasm.`,
      3
    );
  }

  // --- Parked markets -------------------------------------------------------
  const qatarWork = (state.evidence || []).filter((e) => /qatar|bahrain/i.test(`${e.title} ${e.counterparty}`)).length;
  if (qatarWork > 0 && !openException) {
    say(
      'qatar',
      DIRECTION,
      'Do not add a Qatar or Bahrain workstream. There is no external trigger and no approved exception.',
      2
    );
  }

  const consulting = !orgPrimaryDealEnabled(state);

  // --- D001 -----------------------------------------------------------------
  // Revision 01: D001 is produced by the funnel, so its absence is only worth
  // saying when the funnel has not produced anything to choose between.
  if (!consulting && !d001.exists && (!ctx.funnel || ctx.funnel.counts.candidates === 0)) {
    say(
      'd001-none',
      DIRECTION,
      ctx.funnel && ctx.funnel.counts.qualified > 0
        ? `No D001 candidate yet. ${ctx.funnel.counts.qualified} qualified opportunity(ies) exist — the next move is asset information, not more accounts.`
        : 'No D001 candidate yet. Nothing downstream in Dubai matters until the funnel produces one credible sponsor with one named asset.',
      1
    );
  } else if (!consulting && d001.exists) {
    if (d001.criticalUnknown.length) {
      say(
        'd001-unknowns',
        CHALLENGE,
        `${d001.criticalUnknown.length} critical regulated activities are still Unknown (${d001.criticalUnknown.slice(0, 3).map((a) => a.label).join(', ')}). The Execution Gate cannot go green and the deal cannot be executed legally in this state.`,
        1
      );
    }
    if (d001.assetGate === 'green' && d001.capitalGate === 'red') {
      say(
        'd001-capital',
        OBSERVATION,
        'The asset is ready and the capital side is not. A qualified asset with no capital acceptance is an unfunded obligation, not proof.',
        2
      );
    }
    if (!d001.canAdvance && d001.exitRule) {
      say(
        'd001-stage',
        DIRECTION,
        `D001 cannot leave ${d001.stage} yet. Required: ${d001.exitRule.text} Do not move the stage to make the board look better.`,
        2
      );
    }
    if (d001.constraintOverdue) {
      say(
        'd001-constraint',
        CHALLENGE,
        `The D001 constraint — "${d001.constraint.blocker}" — is past its due date. A constraint that slips is the company's actual velocity.`,
        1
      );
    }
  }

  // --- Funnel health (Revision 01) -----------------------------------------
  // The coach now reasons about both deal health and funnel health.
  const f = ctx.funnel;
  if (f) {
    const c = f.counts;

    if (f.primary && c.qualified <= 1) {
      say(
        'funnel-thin-behind',
        CHALLENGE,
        `Primary D001 is designated, but the funnel behind it is thin — ${c.qualified} qualified opportunity(ies) and ${c.backups} backup(s). If this sponsor stalls there is no credible alternative. Spend one founder block this week developing the next two qualified opportunities.`,
        1
      );
    }

    if ((c.contacted || 0) < 3 && (c.tierA || c.qualifiedTargets || 0) >= 1) {
      say(
        'funnel-outreach-insufficient',
        CHALLENGE,
        'Constraint not yet assessable — outreach has not started at sufficient volume.',
        2
      );
    } else if ((c.contacted || 0) >= 3 && (c.engaged || 0) / Math.max(1, c.contacted) < 0.25) {
      say(
        'funnel-access',
        CHALLENGE,
        `${c.contacted} accounts contacted and ${c.engaged} engaged. The current constraint is access and outreach conversion, not product readiness.`,
        2
      );
    }

    if (c.discovery >= 6 && c.qualified <= Math.floor(c.discovery / 5)) {
      say(
        'funnel-qualification',
        CHALLENGE,
        `${c.discovery} commercial conversations produced ${c.qualified} real opportunit${c.qualified === 1 ? 'y' : 'ies'}. Review the ICP and the sponsor value proposition before adding more accounts.`,
        2
      );
    }

    if (!consulting && c.qualified >= 3 && c.assetsIdentified === 0) {
      say(
        'funnel-commitment',
        CHALLENGE,
        `${c.qualified} qualified opportunities and no assets shared. The gap appears to be commitment or trust — or the next step you are proposing is not worth their time.`,
        2
      );
    }

    if (c.universe >= 40 && c.qualifiedTargets <= Math.floor(c.universe * 0.15) && sprint.day >= 10) {
      say(
        'funnel-volume-obsession',
        OBSERVATION,
        `${c.universe} accounts in the universe and only ${c.qualifiedTargets} qualified. Adding names is cheaper than qualifying them, which is exactly why it is the wrong work.`,
        3
      );
    }

    // Comparison discipline: a better-scoring candidate should not lose to
    // whichever opportunity arrived first.
    if (f.primary && f.backups.length) {
      const scored = [f.primary, ...f.backups]
        .map((o) => ({ o, s: candidateTotal(o) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s);
      const best = scored[0];
      const primaryScore = candidateTotal(f.primary);
      if (best && best.o.id !== f.primary.id && best.s - primaryScore >= 5) {
        say(
          'funnel-better-candidate',
          CHALLENGE,
          `${best.o.name} scores ${best.s}/50 against ${primaryScore}/50 for the current Primary. Two candidates are now comparable — do not keep the incumbent as Primary because it arrived first. Either replace it or write down why the score is wrong.`,
          1
        );
      }
    }

    if (f.aging.length >= 3) {
      say(
        'funnel-stagnation',
        OBSERVATION,
        `${f.aging.length} pipeline items have gone stale against your own aging thresholds. Stagnation at this stage is usually a decision you have not made, not a task you have not done.`,
        3
      );
    }

    if (!consulting && !f.primary && c.candidates >= 2) {
      say(
        'funnel-choose',
        DIRECTION,
        `${c.candidates} D001 candidates exist and none has been designated Primary. Comparison without a decision is drift. Designate one, name the backup, and record why.`,
        1
      );
    }

    if (!consulting && !f.primary && c.candidates === 0 && c.qualified === 0 && sprint.day >= 10) {
      say(
        'funnel-empty',
        CHALLENGE,
        `Day ${sprint.day} and the Dubai funnel has produced no qualified opportunity. Nothing downstream — regulated execution, capital, product — can compensate for that.`,
        1
      );
    }
    if (consulting && sprint.day >= 10 && (c.discovery || 0) === 0 && (c.contacted || 0) < 10) {
      say(
        'wedge-behind',
        CHALLENGE,
        `Day ${sprint.day} and the package wedge is behind: ${c.contacted || 0} contacted, ${c.discovery || 0} discoveries. Prioritize Week-1 touches.`,
        1
      );
    }
  }

  // --- Highest-value opportunity -------------------------------------------
  const best = rankOpportunities(state, ctx)[0];
  if (best) {
    say('best-opp', DIRECTION, best.reason, 2);
  }

  // --- Runway ---------------------------------------------------------------
  if (runway.status === 'critical') {
    say(
      'runway',
      CHALLENGE,
      `Runway is ${fmtMonths(runway.months)} at current burn. At minimum viable burn it is ${fmtMonths(runway.minMonths)}. The difference is the decision you have not made yet.`,
      1
    );
  }
  if (runway.proofShare !== null && runway.proofShare < 30 && runway.totalSprintSpend > 0) {
    say(
      'spend-proof',
      OBSERVATION,
      `Only ${runway.proofShare}% of sprint spend is categorised PROVE. Most of the money is buying survival, not evidence.`,
      3
    );
  }
  if (runway.stopSpend > 0) {
    say(
      'spend-stop',
      CHALLENGE,
      `${fmtMoney(runway.stopSpend, cur)} of sprint spend sits in the STOP category. It is labelled correctly and still being spent.`,
      2
    );
  }

  // --- Infrastructure -------------------------------------------------------
  if (infra.live.length > 0 && infra.proposals === 0 && sprint.day >= 15) {
    say(
      'infra-noproposal',
      OBSERVATION,
      `${infra.live.length} infrastructure opportunities and no proposal issued by Day ${sprint.day}. The cash engine is not yet an engine.`,
      3
    );
  }
  if (infra.closest) {
    say(
      'infra-closest',
      DIRECTION,
      `Closest path to cash is ${infra.closest.account || 'an unnamed account'} at ${infra.closest.stage}. Next step: ${infra.closest.nextAction || 'undefined — that is the problem'}.`,
      3
    );
  }

  // --- Top 3 honesty --------------------------------------------------------
  if (top3.lowValue.length >= 3) {
    say(
      'top3-conversion',
      CHALLENGE,
      `${top3.lowValue.length} completed founder actions produced no commercial evidence. That is activity, not execution.`,
      2
    );
  } else if (top3.conversion !== null && top3.graded >= 5 && top3.conversion < 40) {
    say(
      'top3-conversion',
      CHALLENGE,
      `${top3.conversion}% of graded founder actions produced the evidence you expected. Either the actions are wrong or the expectations are.`,
      2
    );
  }
  if (top3.overdue?.length) {
    say(
      'top3-ungraded',
      OBSERVATION,
      `${top3.overdue.length} Critical/High move(s) are overdue. They stay in the execution queue until they are closed.`,
      4
    );
  } else if (top3.waiting?.length >= 5) {
    say(
      'top3-ungraded',
      OBSERVATION,
      `${top3.waiting.length} open moves sit behind Today's commitments. The queue is machinery, not a parking lot.`,
      4
    );
  }

  // --- Gate proximity -------------------------------------------------------
  if (sprint.nextGate && sprint.daysToGate !== null && sprint.daysToGate <= 3) {
    say(
      'gate-near',
      DIRECTION,
      `${sprint.nextGate.label} is ${sprint.daysToGate === 0 ? 'today' : `in ${sprint.daysToGate} day(s)`}. Evidence created after the gate does not count for the gate.`,
      2
    );
  }

  // --- Nothing at all -------------------------------------------------------
  if (out.length === 0) {
    say(
      'quiet',
      OBSERVATION,
      'Nothing in the recorded state contradicts the strategy right now. That is not the same as progress — the evidence ladder is the only score that matters.',
      9
    );
  }

  return out.sort((a, b) => a.priority - b.priority);
}

// Ranks every commercial object by "what should the founder touch next".
export function rankOpportunities(state, ctx) {
  const { sprint, infra, d001 } = ctx;
  const rows = [];

  if (d001.exists) {
    const overdue = d001.constraintOverdue;
    rows.push({
      key: 'd001',
      label: state.d001?.sponsor?.company || 'D001 sponsor',
      score: 100 + Math.max(0, d001.highestEvidence) * 5 + (overdue ? 20 : 0),
      reason: `${state.d001?.sponsor?.company || 'D001'} is your highest-value opportunity because it is ${d001.evidenceLevel}, it is the only named transaction, and its next action ${overdue ? 'is overdue' : 'is open'}.`,
    });
  }

  for (const o of infra.live) {
    const asOf = sprint.executionAsOf;
    const idle = asOf && o.lastTouch ? businessDaysBetween(o.lastTouch, asOf) : 0;
    const w = Math.max(0, weightOf(o.evidenceLevel));
    const score = w * 10 + (o.tier === 'A' ? 15 : o.tier === 'B' ? 5 : 0) + Math.min(idle, 10) + (o.budget ? 10 : 0);
    rows.push({
      key: `infra-${o.id}`,
      label: o.account || 'Unnamed account',
      score,
      reason: `${o.account || 'An unnamed account'} is your highest-value infrastructure opportunity because it is ${o.evidenceLevel || 'E0'}${o.budget ? ', has identified budget' : ''}${idle > 5 ? `, and has been untouched for ${idle} business days` : ''}.`,
    });
  }

  return rows.sort((a, b) => b.score - a.score);
}

// ------------------------------------------------------- specialist views --
// Deterministic worker personas. They structure what you already know and name
// what is missing. They never assert external facts.

export const SPECIALISTS = [
  {
    key: 'scout',
    name: 'Account Scout',
    blurb: 'Finds and ranks target accounts from what is already recorded, and names the gaps in coverage.',
    run(state, ctx) {
      const rows = rankOpportunities(state, ctx);
      const untiered = ctx.infra.live.filter((o) => !o.tier).length;
      const sections = [
        {
          title: 'Ranked accounts',
          items: rows.length ? rows.slice(0, 8).map((r) => `${r.label} — score ${r.score}`) : ['No accounts recorded.'],
        },
        {
          title: 'Coverage gaps',
          items: [
            untiered ? `${untiered} opportunity(ies) have no tier. Tier-A is the only list that gets founder time.` : 'Every opportunity is tiered.',
            ctx.infra.live.length < 10
              ? `Only ${ctx.infra.live.length} live opportunities. A cash engine needs enough named buyers to be tested.`
              : 'Pipeline breadth is adequate to test the offer.',
          ],
        },
      ];

      if (ctx.funnel) {
        const c = ctx.funnel.counts;
        const t = ctx.funnel.targets || {};
        const consulting = !orgPrimaryDealEnabled(state);
        sections.push({
          title: consulting ? 'Consulting funnel against the operating hypothesis' : 'Dubai funnel against the operating hypothesis',
          items: consulting
            ? [
                `Universe ${c.universe}${t.universe ? ` / ${t.universe}` : ''}`,
                `Qualified targets ${c.qualifiedTargets}${t.qualifiedTargets ? ` / ${t.qualifiedTargets}` : ''}`,
                `Active Tier A ${c.tierA}${t.tierA ? ` / ${t.tierA}` : ''}`,
                `Discoveries ${c.discovery}${t.conversations ? ` / ${t.conversations}` : ''}`,
                `Proposals ${c.proposals || 0}${t.proposals ? ` / ${t.proposals}` : ''}, SOW path ${c.sows || 0}`,
              ]
            : [
                `Universe ${c.universe}${t.universe ? ` / ${t.universe}` : ''}`,
                `Qualified targets ${c.qualifiedTargets}${t.qualifiedTargets ? ` / ${t.qualifiedTargets}` : ''}`,
                `Active Tier A ${c.tierA}${t.tierA ? ` / ${t.tierA}` : ''}`,
                `Qualified opportunities ${c.qualified}${t.qualifiedOpportunities ? ` / ${t.qualifiedOpportunities}` : ''}`,
                `D001 candidates ${c.candidates}${t.candidates ? ` / ${t.candidates}` : ''}, Primary ${c.primary}/1`,
              ],
        });
      }

      return sections;
    },
  },
  {
    key: 'intel',
    name: 'Account Intelligence',
    blurb: 'Produces a pre-meeting brief from recorded state, and lists what you still do not know.',
    run(state, ctx) {
      const target = ctx.infra.closest;
      if (!target && !ctx.d001.exists) return [{ title: 'No account selected', items: ['Record an opportunity or name the D001 sponsor first.'] }];
      const o = target || {};
      const known = [
        o.account && `Account: ${o.account}`,
        o.country && `Country: ${o.country}`,
        o.customerType && `Customer type: ${o.customerType}`,
        o.existingPlatform && `Existing platform: ${o.existingPlatform}`,
        o.technologyGap && `Stated gap: ${o.technologyGap}`,
        o.decisionMaker && `Decision-maker: ${o.decisionMaker}`,
        o.evidenceLevel && `Evidence level: ${o.evidenceLevel}`,
      ].filter(Boolean);
      const unknown = [
        !o.decisionMaker && 'Who signs? No decision-maker recorded.',
        !o.budget && 'Is there budget, and whose budget is it?',
        !o.deploymentTiming && 'What is the deployment window?',
        !o.implementationRequirement && 'What must be true technically for them to deploy?',
        'What are they doing today instead, and what does it cost them?',
        'What would make them choose to build rather than buy?',
      ].filter(Boolean);
      return [
        { title: `Brief — ${o.account || 'D001 sponsor'}`, items: known.length ? known : ['Almost nothing is recorded about this account.'] },
        { title: 'Open questions to take into the meeting', items: unknown },
        { title: 'Evidence objective for this meeting', items: [`Leave with at least ${nextLevel(o.evidenceLevel)}. Anything less is a repeat meeting.`] },
      ];
    },
  },
  {
    key: 'regulatory',
    name: 'Regulatory Research',
    blurb: 'Structures the regulated-execution picture and lists the questions that must be answered.',
    disclaimer: 'Research only — not legal advice.',
    run(state, ctx) {
      const unknown = ctx.d001.unknown;
      const assigned = ctx.d001.activities.filter((a) => a.responsible === 'Blocks');
      return [
        {
          title: 'Activities with no named responsible party',
          items: unknown.length ? unknown.map((a) => `${a.label}${a.critical ? ' (critical)' : ''}`) : ['Every activity has a named party.'],
        },
        {
          title: 'Activities Blocks intends to perform itself',
          items: assigned.length
            ? assigned.map((a) => `${a.label} — confirm this does not require a permission Blocks does not hold.`)
            : ['None recorded.'],
        },
        {
          title: 'Questions to put to counsel or the regulator',
          items: [
            'Which of these activities is a regulated activity in this jurisdiction, and under which permission?',
            'Can a licensed partner perform them on our behalf, and what does that require contractually?',
            'What is the concrete next gate, and what evidence does the regulator want to see at it?',
            'What is explicitly out of scope for our current structure?',
          ],
        },
      ];
    },
  },
  {
    key: 'capital',
    name: 'Capital / Partner Scout',
    blurb: 'Tracks licensed counterparties and capital channels against the gate criteria.',
    run(state, ctx) {
      const cap = state.d001?.capital || {};
      const convos = cap.conversations || [];
      return [
        {
          title: 'Capital gate status',
          items: ctx.d001.capitalChecks.map((c) => `${c.ok ? 'MET' : 'NOT MET'} — ${c.label}${c.detail ? ` (${c.detail})` : ''}`),
        },
        {
          title: 'Conversations recorded',
          items: convos.length
            ? convos.map((c) => `${c.name || 'Unnamed'} — ${c.acceptanceSignal ? 'D001-specific acceptance signal' : 'no deal-specific signal yet'}`)
            : ['None. Two credible conversations are the minimum for the gate.'],
        },
        {
          title: 'Partner coverage',
          items: ctx.d001.partners.length
            ? ctx.d001.partners.map((p) => `${p.org || 'Unnamed'} — ${p.stage}${p.permission ? ` (${p.permission})` : ''}`)
            : ['No execution partner identified. This blocks the Execution Gate.'],
        },
      ];
    },
  },
  {
    key: 'proposal',
    name: 'Proposal Copilot',
    blurb: 'Generates the skeleton of a paid readiness, POC, implementation or SaaS proposal from recorded facts.',
    run(state, ctx) {
      const o = ctx.infra.closest || {};
      return [
        {
          title: 'Proposal skeleton',
          items: [
            `1. The problem in their words — ${o.technologyGap || '[not recorded: get this verbatim before writing]'}`,
            `2. What they do today — ${o.existingPlatform || '[not recorded]'}`,
            '3. Scope of the paid engagement — narrow enough to deliver in the sprint',
            `4. Price and payment trigger — ${o.proposalValue ? fmtMoney(o.proposalValue, state.settings?.currency) : '[set a number; an unpriced proposal is not an E4]'}`,
            '5. What Blocks needs from them — data, access, named counterpart',
            '6. Decision date and named approver',
          ],
        },
        {
          title: 'Do not send until',
          items: [
            'A named person has asked for it, or you know exactly who will read it.',
            'It has a price and a decision date.',
            'It says what happens after the POC succeeds.',
          ],
        },
      ];
    },
  },
  {
    key: 'chief',
    name: 'Chief of Staff',
    blurb: 'Proposes today\'s Top 3, the weekly evidence summary, and a stop / continue / concentrate call.',
    run(state, ctx) {
      const f = ctx.funnel;
      return [
        { title: "Proposed Top 3", items: proposeTop3(state, ctx).map((t, i) => `${i + 1}. ${t.text} → expects ${t.expectedEvidence}`) },
        {
          title: 'Funnel state',
          items: f
            ? [
                `Universe ${f.counts.universe} → qualified targets ${f.counts.qualifiedTargets} → Tier A ${f.counts.tierA}`,
                `Engaged ${f.counts.engaged} → discovery ${f.counts.discovery} → qualified opportunities ${f.counts.qualified}`,
                `Assets ${f.counts.assetsIdentified} → candidates ${f.counts.candidates} → Primary ${f.counts.primary}/1 (${f.counts.backups} backup)`,
                `Health: ${f.health.status}. Concentration: ${f.concentration.level}.`,
                f.diagnosis[0] ? `Largest apparent leak: ${f.diagnosis[0].stage}.` : 'No structural leak detected in the recorded data.',
              ]
            : ['No funnel data.'],
        },
        {
          title: 'Evidence this week',
          items: [
            `E4 commercial commitments: ${ctx.evidence.e4Week}`,
            `E5 binding proof: ${ctx.evidence.e5Week}`,
            `Activities logged: ${ctx.evidence.thisWeek.filter((e) => e.level === 'ACT').length}`,
          ],
        },
        { title: 'Stop / Continue / Concentrate', items: stopContinueConcentrate(state, ctx) },
      ];
    },
  },
];

export function proposeTop3(state, ctx) {
  const out = [];
  const { d001, infra, saudi, sprint } = ctx;
  const f = ctx.funnel;
  const queueConfirmed = !!(state?.ui?.founderQueueConfirmedAt || state?.ui?.founderQueueDeferred);

  // Founder Today moves — never multi-day work-package goals (universe build belongs in Pipeline).
  if (f) {
    const c = f.counts;
    const priority = c.tierA || 0;
    if (!queueConfirmed && (c.universe || 0) > 0) {
      out.push({
        text: priority > 0
          ? `Confirm the ${priority} recommended priority accounts for the first outreach wave.`
          : 'Confirm the recommended priority accounts so Bridge can plan named outreach.',
        expectedEvidence: 'OPS',
        whyNow: 'Bridge needs a confirmed priority list before generating named outreach actions.',
      });
    } else if (queueConfirmed && priority > 0 && (c.contacted || 0) < 3) {
      out.push({
        text: 'Verify the decision-maker and contact route for the first five named accounts.',
        expectedEvidence: 'OPS',
        whyNow: 'This prepares the first outreach wave without manufacturing pre-sprint activity.',
        expectedResult: 'Five accounts are ready for personalized outreach on execution start.',
      });
    } else if (c.universe < 20 && priority < 5) {
      // Thin research only — still not a Today work-package; keep as light guidance move.
      out.push({
        text: 'Import or sync enough Dubai accounts to form a 10–15 priority list.',
        expectedEvidence: 'OPS',
      });
    } else if ((c.contacted || 0) >= 1 && (c.engaged || 0) < 5) {
      out.push({ text: 'Open access: get replies or meetings from five priority Dubai accounts.', expectedEvidence: 'E1' });
    } else if ((c.engaged || 0) >= 1 && (c.discovery || 0) < 5) {
      out.push({ text: 'Run substantive commercial conversations with engaged Dubai accounts.', expectedEvidence: 'E2' });
    } else if (c.qualified < 3 && (c.discovery || 0) >= 1) {
      out.push({ text: 'Convert commercial conversations into real opportunities with a named next commercial step.', expectedEvidence: 'E2' });
    } else if (c.assetsIdentified === 0 && c.qualified >= 1) {
      out.push({ text: 'Get one sponsor to name a specific asset and share information about it.', expectedEvidence: 'E3' });
    } else if (c.candidates < 2 && c.assetsIdentified >= 1) {
      out.push({ text: 'Screen the identified assets and produce a second credible D001 candidate.', expectedEvidence: 'E3' });
    } else if (!f.primary && c.candidates >= 1) {
      out.push({ text: 'Compare the D001 candidates and designate a Primary with a named backup.', expectedEvidence: 'E3' });
    } else if (f.primary && f.backups.length === 0) {
      out.push({ text: 'Develop one credible backup candidate behind Primary D001.', expectedEvidence: 'E3' });
    }
  }

  if (out.length < 3 && !d001.exists && (f?.counts?.contacted || 0) > 0) {
    out.push({ text: 'Name one credible Dubai sponsor and secure a first meeting.', expectedEvidence: 'E1' });
  } else if (d001.exists && d001.assetGate !== 'green') {
    const missing = d001.assetChecks.find((c) => !c.ok);
    out.push({ text: `Close the D001 asset gap: ${missing ? missing.label.toLowerCase() : 'complete asset screening'}.`, expectedEvidence: 'E3' });
  } else if (d001.exists && d001.capitalGate !== 'green') {
    out.push({ text: 'Secure a D001-specific acceptance signal from a capital or distribution channel.', expectedEvidence: 'E4' });
  }

  if (d001.criticalUnknown?.length) {
    out.push({
      text: `Get a licensed partner to take responsibility for ${d001.criticalUnknown[0].label.toLowerCase()}.`,
      expectedEvidence: 'E4',
    });
  }

  if (infra.closest && infra.closest.nextAction) {
    out.push({ text: `${infra.closest.account}: ${infra.closest.nextAction}`, expectedEvidence: nextLevel(infra.closest.evidenceLevel) });
  } else if (out.length < 3 && infra.live.length === 0) {
    out.push({ text: 'Name the first five infrastructure buyers who would rather buy Blocks than rebuild it.', expectedEvidence: 'OPS' });
  }

  if (out.length < 3 && saudi.count < 2) {
    out.push({ text: 'Run one Saudi B2B discovery call with a Contribution / CMA / FinTech prospect.', expectedEvidence: 'E2' });
  }
  if (out.length < 3 && d001.constraint?.blocker) {
    out.push({ text: `Work the constraint: ${d001.constraint.blocker}`, expectedEvidence: d001.constraint.evidenceNeeded || 'E3' });
  }

  return out.slice(0, 3);
}

export function stopContinueConcentrate(state, ctx) {
  const items = [];
  const { runway, infra, saudi, time, evidence, funnel } = ctx;
  if (runway.stopSpend > 0) items.push(`STOP: ${fmtMoney(runway.stopSpend, state.settings?.currency)} of spend already classified STOP.`);
  if (infra.customWithoutDeal.length) items.push(`STOP: ${infra.customWithoutDeal.length} CUSTOM product item(s) with no opportunity attached.`);
  if (time.internalShare > 30) items.push(`STOP: ${time.internalShare}% of founder time is internal.`);
  if (evidence.e4 + evidence.e5 > 0) items.push('CONTINUE: the motions that produced E4/E5. Repeat those exact conversations.');
  if (!orgPrimaryDealEnabled(state)) {
    const concentrate = orgReviewConcentrate(state);
    const hints = orgCoachHints(state);
    items.push(
      `CONCENTRATE: ${concentrate?.label || hints?.concentrateDefault || 'Package wedge / Week-1 Tier A'}.`,
    );
    if ((funnel?.counts?.discovery || 0) < 1) {
      items.push('CONTINUE: personalized touches until the first discovery is booked.');
    }
  } else if (saudi.count >= 2) {
    items.push('CONCENTRATE: Saudi has cleared two signals — put the marginal hour there after the primary market.');
  } else {
    const hints = orgCoachHints(state);
    items.push(
      `CONCENTRATE: ${hints?.concentrateDefault || 'Primary deal path'}. It is the only market expected to produce a transaction this sprint.`,
    );
  }
  if (items.length === 0) items.push('Not enough recorded state to make a call. Log evidence and hours first.');
  return items;
}

export function nextLevel(level) {
  const order = ['E0', 'E1', 'E2', 'E3', 'E4', 'E5'];
  const i = order.indexOf(level || 'E0');
  return order[Math.min(order.length - 1, i + 1)];
}

function round(n) {
  return Math.round(num(n) * 10) / 10;
}

// Weekly digest used by the Weekly Review page.
export function weeklyDigest(state, ctx) {
  const { sprint, evidence, runway, time } = ctx;
  const inWeek = (d) => d && withinRange(d, sprint.weekStart, sprint.weekEnd);
  return {
    weekStart: sprint.weekStart,
    weekEnd: sprint.weekEnd,
    evidence: evidence.thisWeek.filter((e) => e.level !== 'ACT'),
    activities: evidence.thisWeek.filter((e) => e.level === 'ACT'),
    e4: evidence.e4Week,
    e5: evidence.e5Week,
    spend: runway.spendThisWeek,
    hours: time.totalHours,
    decisions: (state.decisions || []).filter((d) => inWeek(d.date)),
    gradedActions: (state.top3 || []).filter((t) => (t.completedAt || t.closedAt || t.date) && inWeek(t.completedAt || t.closedAt || t.date) && (t.completionResult || t.outcome)),
    sprintDay: sprint.day,
    sprintStart: sprint.start,
  };
}
